<template>
	<div class="form-layout form-layout-2">
		<div class="report-summary-header mg-b-5 mg-t-0">
			<div>
				<h4 class="tx-inverse mg-b-3 d-block">Mercadorias</h4>
				<p class="mg-b-0"> <i class="icon ion-information-circled mg-r-3"></i> Informações de Mercadorias do Processo de Importação.</p>
			</div>
			<div>
				<a href="#" @click.prevent="newMerchandise" class="btn btn-info"><i class="icon ion-plus tx-22"></i> ADICIONAR</a>
			</div>
		</div>
		<div class="table-responsive mt-4">
			<table class="table display responsive nowrap dataTable no-footer dtr-inline tx-13 table-hover" >
				<thead>
				<tr>
					<th>NCM</th>
					<th>Descrição</th>
					<th style="min-width: 150px;">Órgãos Anuentes</th>
					<th style="min-width: 180px;">Tipo de Licenciamento</th>
					<th style="min-width: 130px;">Nº da LI</th>
					<th>Situação</th>
					<th class="text-center">Ações</th>
				</tr>
				</thead>
				<tbody>
					<tr v-for="merchandise in merchandises" :key="merchandise.id">
						<td>{{ merchandise.ncm | formatNcm}}</td>
						<td>{{ merchandise.description || 'N/I' }}</td>
						<td>{{ merchandise.licensingAuthorities.map((item) => item.acronym).join(', ') || 'N/I' }}</td>
						<td>{{ merchandise.licenseType | formatTypeOfLicense }}</td>
						<td>{{ merchandise.licenseNumber | formatLicenseNumber }}</td>
						<td style="min-width: 180px;">
							{{ merchandise.licenseStatus | formatLicenseStatus }}
						</td>
						<td class="text-center">
							<div class="flex ai-c jc-c fw-nw fgap1">
								<a class="edit-button" href="#" @click.prevent="editMerchandise(merchandise)"><EditIcon :size="16" /></a>
								<a class="remove-button" href="#" @click.prevent="deleteMerchandise(merchandise)"><i class="icon ion-trash-a tx-22"></i></a>
							</div>
						</td>
					</tr>
					<tr v-if="merchandises.length === 0">
						<td colspan="12" class="text-center">
							Nenhuma mercadoria cadastrada.
						</td>
					</tr>
				</tbody>
			</table>
		</div>
		<v-modal
			:handler="openModal"
			:title="`${merchandiseData.id ? 'Atualizar' : 'Adicionar'} Mercadoria`"
			:description="`Processo de Importação ${getTypeOfProcessReferenceByProcess(model)}`"
			:height="100"
			@requestClose="closeModal"
			classModify="merchandise-modal"
		>
			<template #content>
				<div class="flex fd-c fgap2">
					<div class="span-12 merchandise-form">
						<div class="span-12 flex ai-c jc-sb">
							<div class="flex fd-c">
								<v-input 
									name="ncm" 
									label="NCM" 
									:requiredFlag="true" 
									v-model="merchandiseData.ncm" 
									:mask="['####.##.##']" 
									@keyup.enter.native="getAdministrativeTreatment" 
									@blur="getAdministrativeTreatment"
								>
									<template #icon>
										<a 
											href="#" 
											style="width: 45px; height: 45px; display: flex; align-items: center; justify-content: center;" 
											@click.prevent="getAdministrativeTreatment"
											title="Buscar Tratamento Administrativo"
										>
											<SearchIcon :size="16" />
										</a>
									</template>
								</v-input>
								<div class="field-error" v-if="getError('ncm')">
									{{ getError("ncm") }}
								</div>
							</div>
							
							<p style="margin-bottom: 0">Os campos marcados com <span class="tx-danger">*</span> são obrigatórios.</p>
						</div>
						<div class="span-12 flex fd-c">
							<v-textarea name="description" label="Descrição" classModify="description" v-model="merchandiseData.description"></v-textarea>
						</div>
						<div class="span-12 merchandise-form" v-if="showLicenseFields">
							<div class="span-4">
								<select-wrapper label="Órgãos Anuentes" name="licensingAuthoritiesIds" :multiple="true" :options="licensingAuthority" v-model="merchandiseData.licensingAuthoritiesIds"/>
							</div>
							<div class="span-2">
								<select-wrapper classModify="merchandise-select" :clearable="true" label="Tipo de Licenciamento" name="licenseType" :options="typeOfLicenseList" v-model="merchandiseData.licenseType"/>
							</div>
							<div class="span-2">
								<v-input name="licenseNumber" :mask="['##/#######-#']" label="Número da LI" v-model="merchandiseData.licenseNumber"/>
							</div>
							<div class="span-2">
								<date-wrapper label="Data de Registro" name="licenseRegistrationDate" v-model="merchandiseData.licenseRegistrationDate" />
							</div>
						</div>
						<div class="span-12 merchandise-form" v-if="showLicenseFields">
							<div class="span-4">
								<select-wrapper :clearable="true" classModify="merchandise-select" label="Situação" name="licenseStatus" :options="licenseStatusList" v-model="merchandiseData.licenseStatus"/>
							</div>
							<div class="span-2">
								<date-wrapper label="Data da Situação" name="statusDate" v-model="merchandiseData.statusDate" />
							</div>
							<div class="span-2">
								<date-wrapper label="Validade p/ Embarque" name="validityForReleaseDate" v-model="merchandiseData.validityForReleaseDate" />
							</div>
							<div class="span-2">
								<date-wrapper label="Validade p/ Despacho" name="validityForCustomsClearanceDate" v-model="merchandiseData.validityForCustomsClearanceDate" />
							</div>
							<div class="span-2" v-if="merchandiseData.licenseStatus === 'CANCELADA'">
								<date-wrapper label="Data de Cancelamento" name="validityForCancellationDate" v-model="merchandiseData.validityForCancellationDate" />
							</div>
						</div>
					</div>
					<div class="span-12 merchandise-form">
						<div class="span-12 flex ai-c jc-sb fw-w">
							<h4 class="text-dark mb-0">Listagem de Tratamentos Administrativos</h4>
						</div>
						<div class="span-12 merchandise-form merchandise-list-ta" v-for="item in listAdministrativeTreatment" :key="item.id" :class="{ 'ta-enabled': item.enabled }">
							<div class="span-12 flex ai-c jc-sb pb-1" style="border-bottom: 1px solid #e2e2e2;">
								<div class="flex fd-c">
									<h5 class="font-weight-bold text-dark mb-0">
										<div> 
											<span class="font-weight-bold">Destaque </span> 
											<span style="text-transform: uppercase;">{{ item.emphasis | formatEmphasis }} {{ item.emphasisDescription ? ` - ${item.emphasisDescription}` : '' }} </span> 
										</div>
									</h5>
									<span><span class="font-weight-bold"> {{ item.number | formatNcm }}</span> {{ item.description | formatDescription }} </span>
								</div>
								<v-switch label="Ativo" name="enabled" :labels="{ checked: '', unchecked: '' }" v-model="item.enabled" @input="changeAdministrativeTreatment(item)"/>
							</div>
							<div class="span-12 merchandise-form mt-2">
								<div class="span-1" style="min-width: 250px;">
									<span class="font-weight-bold text-dark">Tratamento:</span> {{ item.treatment || 'N/I' }}
								</div>
								<div class="span-1" style="min-width: 350px;">
									<span class="font-weight-bold text-dark">Licenciamento:</span> {{ item.licensing || 'N/I' }}
								</div>
						
								<div class="span-1" style="min-width: 150px;">
									<span class="font-weight-bold text-dark">Anuente:</span> {{ item.licensingAuthority?.acronym || 'N/I' }}
								</div>
								<div class="span-1" style="min-width: 150px;">
									<span class="font-weight-bold text-dark">Finalidade:</span> {{ item.finality || 'N/I' }}
								</div>
								<div class="span-6">
									<span class="font-weight-bold text-dark">Assunto:</span> {{ item.subject || 'N/I' }}
								</div>
								<div class="span-1" style="min-width: 180px">	
									<span class="font-weight-bold text-dark">Vigência Início:</span> {{ item.validityStart | formatDate }}
								</div>
								<div class="span-1" style="min-width: 180px">
									<span class="font-weight-bold text-dark">Vigência Fim:</span> {{ item.validityEnd | formatDate }}
								</div>
							</div>
						</div>
						<div class="span-12 mt-2" v-if="listAdministrativeTreatment.length === 0">	
							<p class="text-center">Nenhum tratamento administrativo encontrado.</p>
						</div>
					</div>
				</div>	
			</template>

			<template #footer>
				<div class="d-flex align-items-center justify-content-end fgap2">
					<!-- <button class="btn btn-secondary" @click.prevent="closeModal">
						CANCELAR
					</button> -->
					<button class="btn btn-primary" @click.prevent="saveMerchandise">
						SALVAR
					</button>
				</div>
			</template>
		</v-modal>
	</div>
</template>

<script>

import Axios from "axios";
import * as yup from "yup";
import moment from "moment";
import Swal from "sweetalert2";
import config from "@/config.js";
import VInput from "@/components/VInput.vue";
import VModal from "@/components/VModal.vue";
import VSwitch from "@/components/VSwitch.vue";
import VTextarea from "@/components/VTextarea.vue";
import { SearchIcon } from '@/components/crm/Icons';
import EditIcon from "@/components/icons/EditIcon.vue";
import DateWrapper from "@/components/DateWrapper.vue";
import ProcessService from "@/services/ProcessService.js";
import SelectWrapper from "@/components/SelectWrapper.vue";
import * as importProcessLists from "@/lists/import-processes-lists.js";
import { swal } from 'vue-swal';

export default {
	name: "list-merchandise",
	props: {
		model: {
			required: true,
		},
	},
	components: {
		VModal,
		VInput,
		SearchIcon,
		VTextarea,
		SelectWrapper,
		DateWrapper,
		VSwitch,
		EditIcon,
	},
	filters: {
		formatDate(value) {
			if (!value) return 'N/I';
			return moment(value).format("DD/MM/YYYY");
		},
		formatTypeOfLicense(value) {
			if (!value) return 'N/I';
			return importProcessLists.typeOfLicenseList.find((item) => item.value === value).label;
		},
		formatLicenseStatus(value) {
			if (!value) return 'N/I';
			return importProcessLists.licenseStatusList.find((item) => item.value === value).label;
		},
		formatNcm(value) {
			if (!value) return 'N/I';
			
			// Remove any non-digit characters
			const digits = value.replace(/\D/g, '');
			
			// Ensure we have between 4 and 10 digits
			if (digits.length < 4 || digits.length > 10) return value;
			
			// Format based on length
			if (digits.length <= 4) {
				return digits;
			} else if (digits.length <= 6) {
				return `${digits.substring(0, 4)}.${digits.substring(4)}`;
			} else if (digits.length <= 8) {
				return `${digits.substring(0, 4)}.${digits.substring(4, 6)}.${digits.substring(6)}`;
			} else {
				return `${digits.substring(0, 4)}.${digits.substring(4, 6)}.${digits.substring(6, 8)}.${digits.substring(8, 10)}`;
			}
		},
		formatEmphasis(value) {
			if (!value) return 'N/I';
			return value.replace(/^destaque\s+/i, '');
		},
		formatDescription(value) {
			if (!value) return '';
			return ' - ' + value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
		},
		formatLicenseNumber(value) {
			if (!value) return 'N/I';
			return `${value.substring(0, 2)}/${value.substring(2, 9)}-${value.substring(8)}`;
		}
	},
	data() {
		return {
			tab: "merchandise",
			openModal: false,
			merchandises: [],
			lastNcmValue: '',
			merchandiseData: {
				ncm: "",
				description: "",
				licenseType: "",
				licensingAuthoritiesIds: [],
				licenseNumber: "",
				licenseRegistrationDate: "",
				licenseStatus: "",
				statusDate: "",
				validityForReleaseDate: "",
				validityForCustomsClearanceDate: "",
				validityForCancellationDate: "",
				administrativeTreatmentNcmIds: [],
			},
			listAdministrativeTreatment: [],
			formErrors: [],
			licensingAuthority:[],
			typeOfLicenseList: [...importProcessLists.typeOfLicenseList],	
			licenseStatusList: [...importProcessLists.licenseStatusList],
			compareTypeOfLicenseList: [
				{ label: 'LICENCIAMENTO NÃO-AUTOMÁTICO', value: 'NOT_AUTOMATIC' },
				{ label: 'LICENCIAMENTO AUTOMÁTICO', value: 'AUTOMATIC' },
			],
		};
	},
	methods: {
		async saveMerchandise() {
			try {
				this.formErrors = [];

				this.$store.commit("setLoading", {
					show: true,
					label: this.modelTitle,
					description: "Salvando, aguarde...",
				});

				await this.schema().validate(this.merchandiseData, {
					abortEarly: false,
				});

				let merchandiseDataModified = {
					...this.merchandiseData,
					processId: this.model.id,
					ncm: this.merchandiseData.ncm.replace(/\D/g, ''),
					licenseNumber: this.merchandiseData?.licenseNumber?.replace(/\D/g, ''),
					administrativeTreatmentNcmIds: this.listAdministrativeTreatment.filter(item => item.enabled).map(item => item.id),
				};

				if (merchandiseDataModified.administrativeTreatmentNcmIds.length === 0) {
					Swal.fire({
						title: "Mercadoria",
						html: '<p>Tem certeza que deseja salvar a mercadoria sem tratamentos administrativos?</p>',
						type: 'warning',
						showConfirmButton: true,
						confirmButtonText: 'Salvar mesmo assim',
						showCancelButton: true,
						cancelButtonText: 'Cancelar',
					}).then(async (result) => {
						if (result.value) {
							await this.eventSaveMerchandise(merchandiseDataModified);
						}
					});
				} else {
					await this.eventSaveMerchandise(merchandiseDataModified);
				}
				
			} catch (e) {
				console.log(e);
				if (e.name && e.name === "ValidationError") {
					this.formErrors = e.inner;
				} else {
					let message = "Erro ao salvar!";
					if (e?.response?.data?.message) {
						message = e.response.data.message;
					}
					Swal.fire({
						title: "Mercadoria",
						text: message,
						type: "error",
					});
				}
			} finally {
				this.$store.commit("setLoading", {
					show: false,
				});
			}
		},
		async eventSaveMerchandise(merchandiseData) {
			let response = {};
			if (merchandiseData?.id) {
				response = await Axios.put(`${config.env.API_DEFAULT.host}/process-products/${merchandiseData.id}`, merchandiseData);
			} else {
				response = await Axios.post(`${config.env.API_DEFAULT.host}/process-products`, merchandiseData);
			}

			await this.closeModal();
			let result = await this.validateMerchandiseGetAdministrativeTreatment();
			this.$emit('showMerchandiseAlert');
			Swal.fire({
				title: "Mercadoria",
				html: "<p style='margin-bottom: 10px;'>Mercadoria salva com sucesso.</p><p style='margin-top: 0px;'>" + result + "</p>",
				type: "success",
			});
		},
		schema() {
			let schema = yup.object().shape({
				ncm: yup.string().required("O NCM é obrigatório!").min(8, "O NCM deve conter no mínimo 8 caracteres!"),
			});
			return schema;
		},
		async deleteMerchandise(merchandise) {
			try {	
				Swal.fire({
					title: "Mercadoria",
					html: '<p>Tem certeza que deseja excluir a mercadoria?</p>',
					type: 'warning',
					showConfirmButton: true,
					confirmButtonText: 'Excluir',
					showCancelButton: true,
					cancelButtonText: 'Cancelar',
				})
				.then(async (result) => {
					if (result.value) {
						this.$store.commit("setLoading", {
							show: true,
							label: "Mercadoria",
							description: "Deletando, aguarde...",
						});
						await Axios.delete(`${config.env.API_DEFAULT.host}/process-products/${merchandise.id}`);
						await this.getProcessProducts();
						let result = await this.validateMerchandiseGetAdministrativeTreatment();			
						Swal.fire({
							title: "Mercadoria",
							html: "<p style='margin-bottom: 10px;'>Mercadoria excluída com sucesso.</p><p style='margin-top: 0px;'>" + result + "</p>",
							type: "success",
						}); 
					}
				});
			} catch (e) {
				console.log(e);
				let message = "Erro ao deletar!";
				if (e?.response?.data?.message) {
					message = e.response.data.message;
				}
				Swal.fire({
					title: "Mercadoria",
					text: message,
					type: "error",
				});
			} finally {
				this.$store.commit("setLoading", {
					show: false,
				});
			}
		},
		changeAdministrativeTreatment(administrativeTreatment) {
			// Verifica se existe uma autoridade de licenciamento com sigla
			if (administrativeTreatment?.licensingAuthority?.acronym) {
				// Encontra a autoridade de licenciamento correspondente na lista de autoridades
				let licensingAuthority = this.licensingAuthority.find(
					item => item.label === administrativeTreatment.licensingAuthority.acronym
				);
				
				// Se o tratamento administrativo está sendo ativado
				if (administrativeTreatment.enabled) {
					// Adiciona o ID da autoridade de licenciamento à lista se ainda não estiver presente
					if (!this.merchandiseData.licensingAuthoritiesIds.includes(licensingAuthority.value)) {
						this.merchandiseData.licensingAuthoritiesIds.push(licensingAuthority.value);
					}
				} 
				// Se o tratamento administrativo está sendo desativado
				else {
					// Filtra outros tratamentos administrativos da mesma autoridade que ainda estão ativos
					let listAdministrativeTreatment = this.listAdministrativeTreatment.filter(
						item => item.licensingAuthority.acronym === administrativeTreatment.licensingAuthority.acronym 
							&& item.id !== administrativeTreatment.id 
							&& item.enabled
					);
					
					// Se não houver mais tratamentos ativos para esta autoridade
					if (listAdministrativeTreatment.length === 0) {
						// Remove o ID da autoridade da lista de IDs
						this.merchandiseData.licensingAuthoritiesIds = 
							this.merchandiseData.licensingAuthoritiesIds.filter(
								id => id !== licensingAuthority.value
							);
					}
				}
			}

			// Verifica se existe um tipo de licenciamento
			if (administrativeTreatment?.licensing != null) {
				// Encontra o tipo de licença correspondente na lista de tipos
				let compareTypeOfLicense = this.compareTypeOfLicenseList.find(
					item => item.label === administrativeTreatment.licensing
				);

				// Se o tratamento está sendo ativado e o tipo de licença foi encontrado
				if (administrativeTreatment.enabled && compareTypeOfLicense) {
					// Se o tipo de licença da mercadoria for diferente do tipo de licença do tratamento administrativo
					if (this.merchandiseData.licenseType != compareTypeOfLicense.value && this.merchandiseData.licenseType) {
						let firstLabel = this.typeOfLicenseList.find(item => item.value === this.merchandiseData.licenseType);
						let secondLabel = this.typeOfLicenseList.find(item => item.value === compareTypeOfLicense.value);
						let html = '<p>O destaque selecionado possui um tipo de licenciamento diferente do cadastrado na mercadoria. Deseja atualizar o licenciamento conforme o novo destaque?</p><p><span class="text-bold text-md"> ' + firstLabel.label + ' </span> <span class="text-md"> para </span> <span class="text-bold text-md"> ' + secondLabel.label + ' </span>.</p>';
						
						Swal.fire({
							title: "Mercadoria",
							html: html,
							type: 'warning',
							showConfirmButton: true,
							confirmButtonText: 'Alterar',
							showCancelButton: true,
							cancelButtonText: 'Cancelar',
						}).then(async (result) => {
							if (result.value) {
								this.merchandiseData.licenseType = compareTypeOfLicense.value;
							}
						});
					} else {
						// Define o tipo de licença da mercadoria
						this.merchandiseData.licenseType = compareTypeOfLicense.value;
					}
				} 
				// Se o tratamento está sendo desativado
				else {
					// Filtra outros tratamentos administrativos do mesmo tipo que ainda estão ativos
					let listAdministrativeTreatment = this.listAdministrativeTreatment.filter(
						item => item.licensing === administrativeTreatment.licensing 
							&& item.id !== administrativeTreatment.id 
							&& item.enabled
					);
					
					// Se não houver mais tratamentos ativos para este tipo de licença
					if (listAdministrativeTreatment.length === 0) {

						let listAdministrativeTreatmentDiff = this.listAdministrativeTreatment.filter(
							item => item.licensing !== administrativeTreatment.licensing 
							&& item.enabled
						);

						if (listAdministrativeTreatmentDiff.length === 0) {
							this.merchandiseData.licenseType = null;
						}

						let removeLicenseType = true;
						listAdministrativeTreatmentDiff.forEach(item => {
							let compareTypeOfLicense = this.compareTypeOfLicenseList.find( itemCompareTypeOfLicenseList => itemCompareTypeOfLicenseList.label === item.licensing );
							if (compareTypeOfLicense) {
								this.merchandiseData.licenseType = compareTypeOfLicense.value;
								removeLicenseType = false;
								return;
							}
						});

						if (removeLicenseType) {
							this.merchandiseData.licenseType = null;
						}
					}
				}
			}
		},
		async diffObjects({field, value}) {
			let modelEnd = {...this.model};
			modelEnd[field] = value;
			const response = await this.$store.dispatch("ProcessStore/diffObjects", {
				initialModel: this.model,
				model: modelEnd,
			});
		},
		async validateMerchandiseGetAdministrativeTreatment() {
			let processAdministrativeTreatment = this.merchandises.filter(item => item.administrativeTreatmentNcms.length > 0);
			if (processAdministrativeTreatment.length > 0 && this.model.importLicense == false) {
				await this.diffObjects({field: 'importLicense', value: true});
				return '<strong>A Licença de Importação <span class="tx-success font-weight-bold">ativada</span> na Aba Geral!</strong>';
			} else if (processAdministrativeTreatment.length === 0 && this.model.importLicense == true) {
				await this.diffObjects({field: 'importLicense', value: false});
				return '<strong>A Licença de Importação <span class="tx-danger font-weight-bold">desativada</span> na Aba Geral!</strong>';
			} else {
				return '';
			}
		},
		async getAdministrativeTreatment() {
			const cleanNcm = this.merchandiseData.ncm.replace(/\D/g, '');

			if (cleanNcm.length !== 8) {
				this.formErrors.push({ path: 'ncm', message: 'NCM deve conter 8 dígitos'});
				this.listAdministrativeTreatment = [];
				return;
			}

			try {
				this.$store.commit('setLoading', {
					show: true,
					label: 'Buscando Tratamento Administrativo...',
				});
				this.formErrors = [];
				this.listAdministrativeTreatment = [];
				const response = await Axios.get(`${config.env.API_DEFAULT.host}/administrative-treatment/ncm?search=${cleanNcm}`);
				const data = response?.data?.data ?? [];
				this.listAdministrativeTreatment = data.sort((a, b) => a.number - b.number);

				if (data.length === 0) {
					Swal.fire({
						title: 'Mercadoria',
						text: 'Nenhum tratamento administrativo encontrado para o NCM informado.',
						type: 'warning',
					});
					this.showLicenseFields = false;
				}

			} catch (error) {
				console.error('Erro ao buscar tratamento administrativo:', error);
				Swal.fire({
					title: 'Mercadoria',
					text: 'Erro ao buscar tratamento administrativo',
					type: 'error',
				});
			} finally {
				this.$store.commit('setLoading', { show: false });
			}
		},
		getTypeOfProcessReferenceByProcess(process) {
			return ProcessService.getTypeOfProcessReference(process);
		},
		async getLicensingAuthority() {
			const response = await Axios.get( `${config.env.API_DEFAULT.host}/licensing-authorities`);
			this.licensingAuthority = [];
			if (response?.data?.data) {
				this.licensingAuthority = response?.data?.data.map((item) => ({
					label: item.acronym,
					value: item.id,
				}));
			}
		},
		async getProcessProducts() { 
			this.$store.commit('setLoading', { show: true, label: 'Buscando Mercadorias...' });
			const response = await Axios.get(`${config.env.API_DEFAULT.host}/process-products?processId=${this.model.id}`);
			const data = response?.data?.data ?? [];
			this.merchandises = data.filter((item) => !item.deletedAt);
			this.$store.commit('setLoading', { show: false });
		},
		async newMerchandise() {
			await this.getLicensingAuthority();
			this.setFabMenuZIndex('unset');
			await this.clearModalData();
			this.openModal = true;
		},
		async editMerchandise(merchandise) {
			await this.clearModalData();
			await this.getLicensingAuthority();
			this.merchandiseData = {
				...merchandise,
				licensingAuthoritiesIds: merchandise.licensingAuthorities.map((item) => item.id),
			};
			await this.getAdministrativeTreatment();
			this.listAdministrativeTreatment = this.listAdministrativeTreatment.map((item) => {
				return {
					...item,
					enabled: merchandise.administrativeTreatmentNcms.some((ncm) => ncm.id === item.id),
				};
			});
			this.setFabMenuZIndex('unset');
			this.openModal = true;
		},
		async closeModal() {
			this.formErrors = [];
			await this.clearModalData();
			this.openModal = false;
			this.setFabMenuZIndex('9999');
			await this.getProcessProducts();
		},
		async clearModalData() {
			this.listAdministrativeTreatment = [];
			this.merchandiseData = {
				ncm: "",
				description: "",
				licenseType: "",
				licensingAuthoritiesIds: [],
				licenseNumber: "",
				licenseRegistrationDate: "",
				licenseStatus: "",
				statusDate: "",
				validityForReleaseDate: "",
				validityForCustomsClearanceDate: "",
				validityForCancellationDate: "",
				administrativeTreatmentNcmIds: [],
			};
		},
		getError(field) {
			if (this.formErrors.length > 0) {
				const item = this.formErrors.find((e) => e.path === field || e.type === field);
				if (item && item.message) {
					return this.formErrors.find((e) => e.path === field || e.type === field).message;
				}
			} else {
				return null;
			}
		},
		setFabMenuZIndex(zIndex) {
			const fabMenu = document.querySelector('.fab-menu');
			if (fabMenu) {
				fabMenu.style.zIndex = zIndex;
			}
		},
	},
	mounted() {
		this.getProcessProducts();
	},
	watch: {
		'listAdministrativeTreatment': {
			deep: true,
			handler() {
				this.showLicenseFields = this.listAdministrativeTreatment.some(item => item.enabled);
			}
		}
	}
};
</script>

<style scoped>

.merchandise-form {
	display: grid;
	grid-template-columns: repeat(12, 1fr);
	grid-gap: 15px;
	align-content: start;
}

.merchandise-form .field-error {
	padding: 0.25rem;
	color: var(--danger);
}

.merchandise-form-column {
    display: flex;
    flex-direction: column;
    gap: 15px;
}

.merchandise-list-ta {
    border: 1px solid #e2e2e2;
    padding:10px;
    border-radius: 8px;
	background-color: white;
	grid-gap: 5px;
}

.ta-enabled {
	background-color: rgb(230 241 255 / 0.45);
}

</style>

<style>
.merchandise-modal {
	max-width: unset !important;
}

.top-down .dropdown-menu {
	top: auto !important;
	bottom: 100% !important;
	margin-bottom: 10px !important;
}

.description textarea {
	min-height: 90px !important;
}

.merchandise-select .v-select .dropdown-toggle .clear {
	top: 8px !important;
    left: 8px !important;
	font-size: 23px !important;
}

</style>
